/**
 * @file Join-screen card for people who opened a call link inside another
 * app's built-in browser (Instagram, Gmail, LINE, …).
 *
 * Design context: calls break in built-in browsers, so the card stands in
 * for the join screen until the person chooses: open the call in Safari /
 * Chrome, copy the link, or continue anyway. It sits in front of the join
 * screen on purpose: the join screen asks for camera and microphone on
 * mount, and that prompt is best left to the real browser.
 *
 * "Open in …" cannot report whether the app let the hand-off through, so if
 * the page is still in front ~1.5 s later the manual steps appear. "Continue
 * anyway" is remembered for the tab so a re-render does not bring the card
 * back. The WeCo mobile app joins natively and never reaches this page.
 */
import { useEffect, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  chromeHandoffUrl,
  detectInAppBrowser,
  safariHandoffUrl,
  type InAppBrowser,
} from "../lib/inAppBrowser";

export const IN_APP_CONTINUE_KEY = "weco.call.inAppContinue";
const HANDOFF_FALLBACK_MS = 1500;

function readContinued(): boolean {
  try {
    return sessionStorage.getItem(IN_APP_CONTINUE_KEY) === "1";
  } catch {
    return false;
  }
}

async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // Some built-in browsers have no async clipboard; fall back to a selection copy.
    try {
      const area = document.createElement("textarea");
      area.value = text;
      area.setAttribute("readonly", "");
      area.style.position = "fixed";
      area.style.opacity = "0";
      document.body.appendChild(area);
      area.select();
      const ok = document.execCommand("copy");
      area.remove();
      return ok;
    } catch {
      return false;
    }
  }
}

interface InAppBrowserGateProps {
  children: ReactNode;
  /** Injected in tests; defaults to the real user agent. */
  userAgent?: string;
  /** Fill the viewport (the guest page has no shell around it). */
  fullScreen?: boolean;
}

export function InAppBrowserGate({
  children,
  userAgent,
  fullScreen,
}: InAppBrowserGateProps) {
  const [inApp] = useState<InAppBrowser | null>(() =>
    detectInAppBrowser(
      userAgent ??
        (typeof navigator === "undefined" ? "" : navigator.userAgent),
    ),
  );
  const [continued, setContinued] = useState(readContinued);

  if (!inApp || continued) return <>{children}</>;
  return (
    <InAppBrowserCard
      inApp={inApp}
      fullScreen={fullScreen}
      onContinue={() => {
        try {
          sessionStorage.setItem(IN_APP_CONTINUE_KEY, "1");
        } catch {
          /* private mode: the card just shows again on reload */
        }
        setContinued(true);
      }}
    />
  );
}

function InAppBrowserCard({
  inApp,
  fullScreen,
  onContinue,
}: {
  inApp: InAppBrowser;
  fullScreen?: boolean;
  onContinue: () => void;
}) {
  const { t } = useTranslation();
  const link = window.location.href;
  const browser =
    inApp.platform === "ios"
      ? "Safari"
      : inApp.platform === "android"
        ? "Chrome"
        : null;
  const [showSteps, setShowSteps] = useState(browser === null);
  const timer = useRef<number | undefined>(undefined);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  const openInBrowser = () => {
    const target =
      inApp.platform === "ios"
        ? safariHandoffUrl(link)
        : chromeHandoffUrl(link);
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => {
      if (document.visibilityState === "visible") setShowSteps(true);
    }, HANDOFF_FALLBACK_MS);
    window.location.href = target;
  };

  const copyLink = async () => {
    if (await copyText(link)) {
      toast.success(
        t("call.inAppBrowser.copied", {
          browser: browser ?? t("call.inAppBrowser.yourBrowser"),
        }),
      );
    } else {
      setShowSteps(true);
      toast.error(t("call.inAppBrowser.copyFailed"));
    }
  };

  const steps = [
    t(
      inApp.platform === "android"
        ? "call.inAppBrowser.stepMenuAndroid"
        : "call.inAppBrowser.stepMenuIos",
    ),
    t("call.inAppBrowser.stepOpen", {
      browser: browser ?? t("call.inAppBrowser.yourBrowser"),
    }),
    t("call.inAppBrowser.stepCopy", {
      browser: browser ?? t("call.inAppBrowser.yourBrowser"),
    }),
  ];

  return (
    <div
      className={`${fullScreen ? "min-h-screen" : "flex-1 min-h-0"} flex items-center justify-center bg-background p-6 overflow-y-auto`}
      data-testid="in-app-browser-card"
    >
      <div className="w-full max-w-sm flex flex-col gap-4">
        <h1 className="text-2xl font-bold text-foreground">
          {browser
            ? t("call.inAppBrowser.title", { browser })
            : t("call.inAppBrowser.titleOther")}
        </h1>
        <p className="text-sm text-muted-foreground">
          {inApp.app
            ? t("call.inAppBrowser.bodyNamed", { app: inApp.app })
            : t("call.inAppBrowser.bodyUnnamed")}
        </p>

        {browser && (
          <button
            type="button"
            onClick={openInBrowser}
            className="h-12 bg-primary text-primary-foreground font-bold rounded-2xl shadow-sm"
          >
            {t("call.inAppBrowser.open", { browser })}
          </button>
        )}
        <button
          type="button"
          onClick={() => void copyLink()}
          className="h-12 border border-border font-bold rounded-2xl hover:bg-muted transition-colors text-foreground"
        >
          {t("call.inAppBrowser.copyLink")}
        </button>

        {showSteps && (
          <div
            className="rounded-2xl bg-muted p-4 text-sm text-foreground"
            data-testid="in-app-browser-steps"
          >
            {browser && (
              <p className="font-semibold mb-2">
                {t("call.inAppBrowser.stepsTitle", { browser })}
              </p>
            )}
            <ol className="list-decimal ps-5 flex flex-col gap-1">
              {steps.map((step) => (
                <li key={step}>{step}</li>
              ))}
            </ol>
            <p
              className="mt-3 text-xs text-muted-foreground break-all select-all"
              dir="ltr"
            >
              {link}
            </p>
          </div>
        )}

        <button
          type="button"
          onClick={onContinue}
          className="h-10 text-sm font-semibold text-muted-foreground hover:text-foreground"
        >
          {t("call.inAppBrowser.continue")}
        </button>
      </div>
    </div>
  );
}
